import React from 'react';
import { 
  LogIn, 
  LogOut, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  DollarSign,
  Home,
  Sparkles,
  TrendingUp,
  Users
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const OperationsStats = ({ stats = {} }) => {
  const { t } = useTranslation();

  const statCards = [
    {
      title: t('totalOperations'),
      value: ((stats.totalCheckIns || 0) + (stats.totalCheckOuts || 0)).toString(),
      subtitle: `${stats.totalCheckIns || 0} ${t('checkIns')} • ${stats.totalCheckOuts || 0} ${t('checkOuts')}`,
      icon: <Users size={20} />,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-200'
    },
    {
      title: t('pendingOperations'),
      value: ((stats.pendingCheckIns || 0) + (stats.pendingCheckOuts || 0)).toString(),
      subtitle: `${(stats.pendingCheckIns || 0) + (stats.pendingCheckOuts || 0)} ${t('statsPendingOperations')}`,
      icon: <Clock size={20} />,
      color: 'yellow',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600',
      borderColor: 'border-yellow-200'
    },
    {
      title: t('confirmedOperations'),
      value: ((stats.confirmedCheckIns || 0) + (stats.confirmedCheckOuts || 0)).toString(),
      subtitle: `${(stats.confirmedCheckIns || 0) + (stats.confirmedCheckOuts || 0)} ${t('statsConfirmedOperations')}`,
      icon: <CheckCircle size={20} />,
      color: 'green',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      borderColor: 'border-green-200'
    },
    {
      title: t('overdueOperations'),
      value: ((stats.overdueCheckIns || 0) + (stats.overdueCheckOuts || 0)).toString(),
      subtitle: `${stats.overdueCheckIns || 0} ${t('checkInOperations')} • ${stats.overdueCheckOuts || 0} ${t('checkOutOperations')}`,
      icon: <AlertTriangle size={20} />,
      color: 'red',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
      borderColor: 'border-red-200'
    },
    {
      title: t('additionalRevenue'),
      value: `${(stats.additionalRevenue || 0).toLocaleString()} ${t('EGP')}`,
      subtitle: `${(stats.earlyOperations || 0) + (stats.lateOperations || 0)} ${t('statsFeeOperations')}`,
      icon: <DollarSign size={20} />,
      color: 'purple',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      borderColor: 'border-purple-200'
    },
    {
      title: t('roomsReady'),
      value: (stats.roomsReady || 0).toString(),
      subtitle: `${stats.roomsCleaning || 0} ${t('roomsCleaning')}`,
      icon: <Home size={20} />,
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      borderColor: 'border-emerald-200'
    }
  ];

  return (
    <div className="mb-4 sm:mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-6">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className={`${stat.bgColor} rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 border ${stat.borderColor} hover:shadow-md transition-all duration-200`}
          >
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-slate-600 text-xs sm:text-sm font-medium mb-1 sm:mb-2 truncate">
                  {stat.title}
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 mb-1">
                  {stat.value}
                </p>
                <p className="text-xs text-slate-500 leading-tight">
                  {stat.subtitle}
                </p>
              </div>
              <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl ${stat.bgColor} ${stat.textColor} ml-2 sm:ml-3 rtl:mr-2 sm:rtl:mr-3 rtl:ml-0`}>
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Performance Insights */}
      {((stats.earlyOperations || 0) > 0 || (stats.lateOperations || 0) > 0 || (stats.noShowOperations || 0) > 0) && (
        <div className="mt-4 sm:mt-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200">
          <div className="flex items-center space-x-3 rtl:space-x-reverse mb-3 sm:mb-4">
            <TrendingUp size={20} className="text-blue-600" />
            <h3 className="text-base sm:text-lg font-semibold text-blue-900">
              {t('performanceInsights')}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {(stats.earlyOperations || 0) > 0 && (
              <div className="bg-white rounded-lg p-3 sm:p-4 border border-blue-200">
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-blue-700">{stats.earlyOperations}</div>
                  <div className="text-xs sm:text-sm text-blue-600">{t('earlyOperations')}</div>
                </div>
              </div>
            )}
            
            {(stats.lateOperations || 0) > 0 && (
              <div className="bg-white rounded-lg p-3 sm:p-4 border border-orange-200">
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-orange-700">{stats.lateOperations}</div>
                  <div className="text-xs sm:text-sm text-orange-600">{t('lateOperations')}</div>
                </div>
              </div>
            )}
            
            {(stats.noShowOperations || 0) > 0 && (
              <div className="bg-white rounded-lg p-3 sm:p-4 border border-red-200">
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-red-700">{stats.noShowOperations}</div>
                  <div className="text-xs sm:text-sm text-red-600">{t('noShowOperations')}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};